import { useState, useEffect, useCallback } from "react";
import api from "../../services/api";

const STATUS_MAP = {
  draft:     { label: "Brouillon",  cls: "draft" },
  sent:      { label: "Envoyée",    cls: "warning" },
  partial:   { label: "Partielle",  cls: "warning" },
  received:  { label: "Reçue",      cls: "success" },
  cancelled: { label: "Annulée",    cls: "danger" },
};
const STATUS_FLOW = { draft: ["sent", "cancelled"], sent: ["partial", "received", "cancelled"], partial: ["received", "cancelled"] };

function fmtDec(n) { return (n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

export default function OrdersTab({ currentCompanyId, products = [], suppliers = [] }) {
  const [orders, setOrders]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [showReceive, setShowReceive] = useState(null);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState("");

  // Formulaire commande
  const [orderForm, setOrderForm] = useState({ supplier_id: "", order_date: "", expected_date: "", notes: "" });
  const [lines, setLines] = useState([{ product_id: "", quantity: "", unit_price: "" }]);

  // Suggestions réapprovisionnement
  const [suggestions, setSuggestions] = useState([]);

  const load = useCallback(() => {
    if (!currentCompanyId) return;
    setLoading(true);
    api.stocks.orders(currentCompanyId, { status: statusFilter })
      .then(r => setOrders(Array.isArray(r) ? r : []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [currentCompanyId, statusFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (currentCompanyId) api.stocks.orderSuggests(currentCompanyId).then(setSuggestions).catch(() => {});
  }, [currentCompanyId]);

  const openCreate = (prefilledLines = null) => {
    setOrderForm({ supplier_id: "", order_date: new Date().toISOString().split('T')[0], expected_date: "", notes: "" });
    setLines(prefilledLines || [{ product_id: "", quantity: "", unit_price: "" }]);
    setError(""); setShowCreate(true);
  };

  const openFromSuggestion = (s) => {
    openCreate([{ product_id: String(s.id), quantity: String(Math.max(s.stock_reorder - s.stock_current + 1, 1)), unit_price: String(s.purchase_price) }]);
    setOrderForm(f => ({ ...f, supplier_id: String(s.supplier_id || "") }));
  };

  const addLine = () => setLines(l => [...l, { product_id: "", quantity: "", unit_price: "" }]);
  const removeLine = (i) => setLines(l => l.filter((_, idx) => idx !== i));
  const updateLine = (i, field, val) => setLines(l => l.map((ln, idx) => idx === i ? { ...ln, [field]: val } : ln));

  const handleCreateOrder = async (e) => {
    e.preventDefault(); setError(""); setSaving(true);
    const validLines = lines.filter(l => l.product_id && l.quantity > 0);
    if (!validLines.length) { setError("Ajoutez au moins une ligne."); setSaving(false); return; }
    try {
      await api.stocks.createOrder({
        company_id: currentCompanyId, supplier_id: orderForm.supplier_id || null,
        order_date: orderForm.order_date, expected_date: orderForm.expected_date || null,
        notes: orderForm.notes || null,
        lines: validLines.map(l => ({ product_id: parseInt(l.product_id), quantity: parseFloat(l.quantity), unit_price: parseFloat(l.unit_price) || 0 })),
      });
      setShowCreate(false); load();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleStatusChange = async (id, status) => {
    await api.stocks.orderStatus(id, status).catch(console.error);
    load();
  };

  const handleCancel = async (id) => {
    if (!window.confirm("Annuler cette commande ?")) return;
    await api.stocks.cancelOrder(id).catch(console.error);
    load();
  };

  // Réception
  const [receiveOrder, setReceiveOrder] = useState(null);
  const [receiveCounts, setReceiveCounts] = useState({});

  const openReceive = async (orderId) => {
    const ord = await api.stocks.orderById(orderId).catch(() => null);
    if (!ord) return;
    const init = {};
    ord.lines?.forEach(l => { init[l.id] = String(l.quantity_ordered - l.quantity_received); });
    setReceiveCounts(init);
    setReceiveOrder(ord);
  };

  const handleReceive = async () => {
    if (!receiveOrder) return;
    setSaving(true); setError("");
    try {
      const received_lines = receiveOrder.lines?.map(l => ({
        line_id: l.id, product_id: l.product_id,
        quantity_received: parseFloat(receiveCounts[l.id]) || 0,
        unit_price: l.unit_price,
      })) || [];
      await api.stocks.receiveOrder(receiveOrder.id, { received_lines, received_date: new Date().toISOString().split('T')[0] });
      setReceiveOrder(null); load();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const lineTotal = lines.reduce((s, l) => s + (parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0), 0);

  return (
    <div>
      {/* Suggestions */}
      {suggestions.length > 0 && (
        <div className="card" style={{ marginBottom: 16, background: "#ba751708", borderColor: "#ba751733" }}>
          <div style={{ fontWeight: 700, marginBottom: 10, color: "#ba7517" }}>⚠️ {suggestions.length} produit(s) à réapprovisionner</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {suggestions.slice(0, 6).map(s => (
              <div key={s.id} style={{ background: "#fff", border: "var(--border)", borderRadius: 8, padding: "8px 12px", fontSize: 12, cursor: "pointer" }}
                onClick={() => openFromSuggestion(s)}>
                <div style={{ fontWeight: 700 }}>{s.name}</div>
                <div style={{ color: "var(--text-muted)" }}>{s.stock_current}/{s.stock_reorder} {s.unit}</div>
                <div style={{ color: "var(--g)", fontWeight: 600, marginTop: 2 }}>+ Créer commande</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filtres */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        {[["", "Toutes"], ["draft", "Brouillon"], ["sent", "Envoyées"], ["partial", "Partielles"], ["received", "Reçues"], ["cancelled", "Annulées"]].map(([id, lbl]) => (
          <button key={id} onClick={() => setStatusFilter(id)} style={{ padding: "5px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer", border: "var(--border)", background: statusFilter === id ? "var(--g)" : "#fff", color: statusFilter === id ? "#fff" : "var(--text-muted)" }}>{lbl}</button>
        ))}
        <button className="btn primary" style={{ marginLeft: "auto" }} onClick={() => openCreate()}>+ Nouvelle commande</button>
      </div>

      {error && <div className="alert danger" style={{ marginBottom: 12 }}>{error}</div>}

      <div className="card">
        <div className="tbl-overflow">
          <table className="tbl">
            <thead>
              <tr><th>Réf.</th><th>Fournisseur</th><th>Date</th><th>Attendue</th><th>Lignes</th><th>Total HT</th><th>Statut</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={{ textAlign: "center", padding: 24 }}>Chargement…</td></tr>
              ) : orders.length === 0 ? (
                <tr><td colSpan={8} style={{ textAlign: "center", padding: 24, color: "var(--text-muted)" }}>Aucune commande fournisseur</td></tr>
              ) : orders.map(o => (
                <tr key={o.id}>
                  <td style={{ fontFamily: "monospace", fontWeight: 700, color: "var(--g)", fontSize: 12 }}>{o.order_ref}</td>
                  <td style={{ fontWeight: 600 }}>{o.supplier_name || "—"}</td>
                  <td style={{ color: "var(--text-muted)" }}>{o.order_date && new Date(o.order_date).toLocaleDateString("fr-FR")}</td>
                  <td style={{ color: "var(--text-muted)" }}>{o.expected_date ? new Date(o.expected_date).toLocaleDateString("fr-FR") : "—"}</td>
                  <td style={{ textAlign: "center" }}>{o.line_count}</td>
                  <td style={{ fontWeight: 700 }}>{fmtDec(o.total_ht)} €</td>
                  <td><span className={`badge ${STATUS_MAP[o.status]?.cls}`}>{STATUS_MAP[o.status]?.label}</span></td>
                  <td>
                    <div style={{ display: "flex", gap: 4 }}>
                      {["sent", "partial"].includes(o.status) && (
                        <button className="btn sm" style={{ color: "var(--g)", fontSize: 11 }} onClick={() => openReceive(o.id)}>📥 Réceptionner</button>
                      )}
                      {STATUS_FLOW[o.status]?.includes("sent") && (
                        <button className="btn sm" style={{ fontSize: 11 }} onClick={() => handleStatusChange(o.id, "sent")}>📤 Envoyer</button>
                      )}
                      {!["received", "cancelled"].includes(o.status) && (
                        <button className="btn sm" style={{ color: "var(--red)" }} onClick={() => handleCancel(o.id)} title="Annuler">✕</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal créer commande */}
      {showCreate && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowCreate(false)}>
          <div className="modal" style={{ maxWidth: 700 }}>
            <div className="modal-header">
              <h3 className="modal-title">📦 Nouvelle commande fournisseur</h3>
              <button className="modal-close" onClick={() => setShowCreate(false)}>✕</button>
            </div>
            {error && <div className="alert danger" style={{ marginBottom: 12 }}>{error}</div>}
            <form onSubmit={handleCreateOrder}>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Fournisseur</label>
                  <select className="form-control" value={orderForm.supplier_id} onChange={e => setOrderForm(f => ({ ...f, supplier_id: e.target.value }))}>
                    <option value="">— Aucun —</option>
                    {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Date commande</label>
                  <input type="date" className="form-control" value={orderForm.order_date} onChange={e => setOrderForm(f => ({ ...f, order_date: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Date livraison prévue</label>
                  <input type="date" className="form-control" value={orderForm.expected_date} onChange={e => setOrderForm(f => ({ ...f, expected_date: e.target.value }))} />
                </div>
              </div>

              <div style={{ fontWeight: 700, marginBottom: 8 }}>Lignes de commande</div>
              {lines.map((l, i) => {
                const p = products.find(pr => pr.id === parseInt(l.product_id));
                return (
                  <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "flex-end" }}>
                    <div className="form-group" style={{ flex: 2, marginBottom: 0 }}>
                      {i === 0 && <label className="form-label">Produit</label>}
                      <select className="form-control" required value={l.product_id} onChange={e => { const pr = products.find(pr => pr.id === parseInt(e.target.value)); updateLine(i, 'product_id', e.target.value); if (pr) updateLine(i, 'unit_price', String(pr.purchase_price)); }}>
                        <option value="">— Produit —</option>
                        {products.map(pr => <option key={pr.id} value={pr.id}>{pr.name} ({pr.sku})</option>)}
                      </select>
                    </div>
                    <div className="form-group" style={{ width: 100, marginBottom: 0 }}>
                      {i === 0 && <label className="form-label">Qté</label>}
                      <input type="number" step="0.001" min="0.001" required className="form-control" placeholder="0" value={l.quantity} onChange={e => updateLine(i, 'quantity', e.target.value)} />
                    </div>
                    <div className="form-group" style={{ width: 110, marginBottom: 0 }}>
                      {i === 0 && <label className="form-label">PA HT (€)</label>}
                      <input type="number" step="0.01" min="0" className="form-control" placeholder="0.00" value={l.unit_price} onChange={e => updateLine(i, 'unit_price', e.target.value)} />
                    </div>
                    <div style={{ paddingBottom: 2 }}>
                      {i === 0 && <div style={{ height: 22 }} />}
                      <button type="button" className="btn sm" style={{ color: "var(--red)" }} onClick={() => removeLine(i)} disabled={lines.length === 1}>✕</button>
                    </div>
                  </div>
                );
              })}
              <button type="button" className="btn" style={{ fontSize: 12, marginBottom: 16 }} onClick={addLine}>+ Ajouter une ligne</button>

              <div style={{ display: "flex", justifyContent: "flex-end", fontWeight: 700, marginBottom: 12 }}>
                Total HT : {fmtDec(lineTotal)} €
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <input className="form-control" placeholder="Remarques…" value={orderForm.notes} onChange={e => setOrderForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
              <div className="btn-group" style={{ justifyContent: "flex-end" }}>
                <button type="button" className="btn" onClick={() => setShowCreate(false)}>Annuler</button>
                <button type="submit" className="btn primary" disabled={saving}>{saving ? "Création…" : "Créer la commande"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal réception */}
      {receiveOrder && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setReceiveOrder(null)}>
          <div className="modal" style={{ maxWidth: 600 }}>
            <div className="modal-header">
              <h3 className="modal-title">📥 Réception — {receiveOrder.order_ref}</h3>
              <button className="modal-close" onClick={() => setReceiveOrder(null)}>✕</button>
            </div>
            {error && <div className="alert danger" style={{ marginBottom: 12 }}>{error}</div>}
            <div style={{ marginBottom: 12, fontSize: 12, color: "var(--text-muted)" }}>Fournisseur : <strong>{receiveOrder.supplier_name || "—"}</strong></div>
            <div className="tbl-overflow">
              <table className="tbl">
                <thead><tr><th>Produit</th><th>Commandé</th><th>Déjà reçu</th><th>Quantité à recevoir</th></tr></thead>
                <tbody>
                  {receiveOrder.lines?.map(l => (
                    <tr key={l.id}>
                      <td><div style={{ fontWeight: 600 }}>{l.product_name}</div><div style={{ fontSize: 11, color: "var(--text-muted)" }}>{l.sku}</div></td>
                      <td>{l.quantity_ordered} {l.unit}</td>
                      <td style={{ color: l.quantity_received > 0 ? "var(--g)" : "var(--text-muted)" }}>{l.quantity_received}</td>
                      <td>
                        <input type="number" step="0.001" min="0" className="form-control" style={{ width: 100 }}
                          value={receiveCounts[l.id] ?? ""}
                          onChange={e => setReceiveCounts(c => ({ ...c, [l.id]: e.target.value }))} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="btn-group" style={{ justifyContent: "flex-end", marginTop: 12 }}>
              <button className="btn" onClick={() => setReceiveOrder(null)}>Annuler</button>
              <button className="btn primary" disabled={saving} onClick={handleReceive}>{saving ? "Enregistrement…" : "Valider la réception"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
